import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useApplySimulationSelection,
  usePreviewSimulationSelection,
  type SimulationBulkAction,
  type SimulationBulkKind,
  type SimulationBulkPreview,
} from '@/hooks/use-platform-simulation'

const NOUNS: Record<SimulationBulkKind, [string, string]> = {
  user: ['user', 'users'],
  organisation: ['organisation', 'organisations'],
}

interface BulkSimulationBarProps {
  kind: SimulationBulkKind
  selectedIds: string[]
  onClear: () => void
}

/** Bulk mark-as-simulated and delete for a Platform list, with a confirm step that lists every row. */
export function BulkSimulationBar({ kind, selectedIds, onClear }: BulkSimulationBarProps) {
  const preview = usePreviewSimulationSelection()
  const apply = useApplySimulationSelection()
  const [plan, setPlan] = useState<SimulationBulkPreview | null>(null)
  const [singular, plural] = NOUNS[kind]

  if (selectedIds.length === 0) return null

  async function onPreview(action: SimulationBulkAction) {
    try {
      setPlan(await preview.mutateAsync({ kind, action, ids: selectedIds }))
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not preview the change')
    }
  }

  async function onConfirm() {
    if (!plan) return
    try {
      const ids = plan.rows.filter((row) => row.changes).map((row) => row.id)
      await apply.mutateAsync({ kind, action: plan.action, ids })
      toast.success(
        plan.action === 'mark'
          ? `Marked ${ids.length} ${ids.length === 1 ? singular : plural} as simulated`
          : `Deleted ${ids.length} ${ids.length === 1 ? singular : plural}`,
      )
      setPlan(null)
      onClear()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not apply the change')
    }
  }

  const isDelete = plan?.action === 'delete'

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
      <span className="text-sm font-medium">
        {selectedIds.length} {selectedIds.length === 1 ? singular : plural} selected
      </span>
      <div className="ml-auto flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onClear}>
          Clear
        </Button>
        <Button type="button" size="sm" disabled={preview.isPending} onClick={() => void onPreview('mark')}>
          Mark as simulated
        </Button>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          disabled={preview.isPending}
          onClick={() => void onPreview('delete')}
        >
          Delete
        </Button>
      </div>
      <AlertDialog open={plan !== null} onOpenChange={(open) => (open ? null : setPlan(null))}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isDelete ? 'Delete' : 'Mark as simulated'}: {plan?.will_change ?? 0} of {plan?.rows.length ?? 0}{' '}
              {plural}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isDelete
                ? 'Deleted rows cannot be restored. Rows marked Kept are not changed.'
                : 'Marked rows are hidden from Platform lists and reports. Rows marked Kept are not changed.'}{' '}
              The action is logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="max-h-72 space-y-2 overflow-y-auto text-sm">
            {plan?.rows.map((row) => (
              <li key={row.id} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate">{row.label}</div>
                  {row.warning ? <div className="text-xs text-muted-foreground">Note: {row.warning}</div> : null}
                </div>
                {row.changes ? (
                  <Badge variant={isDelete ? 'destructive' : 'secondary'}>{isDelete ? 'Delete' : 'Mark'}</Badge>
                ) : (
                  <Badge variant="outline" title={row.reason}>
                    Kept: {row.reason}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant={isDelete ? 'destructive' : 'default'}
              disabled={apply.isPending || !plan?.will_change}
              onClick={() => void onConfirm()}
            >
              {apply.isPending ? 'Working…' : isDelete ? 'Delete rows' : 'Mark rows'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
